import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { getIssues, getIssue } from "../../api/issuesApi";
import { getFeaturedTopics, setFeaturedTopic, clearFeaturedTopic } from "../../api/featuredApi";
import { translateAuthError } from "../../api/authApi";

const POSITION_LABELS = { 1: "کارت چپ", 2: "کارت وسط (برجسته)", 3: "کارت راست" };

// One of the three homepage slots: shows the currently picked card (if
// any) plus an issue → card picker to set or replace it.
function FeaturedSlot({ position, current, issues, token, onChanged }) {
  const [issueId, setIssueId] = useState("");
  const [topics, setTopics] = useState([]);
  const [topicId, setTopicId] = useState("");
  const [loadingTopics, setLoadingTopics] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!issueId) {
      setTopics([]);
      setTopicId("");
      return;
    }
    setLoadingTopics(true);
    getIssue(issueId)
      .then((iss) => setTopics(iss.topics))
      .catch(() => setTopics([]))
      .finally(() => setLoadingTopics(false));
  }, [issueId]);

  async function handleSet(event) {
    event.preventDefault();
    if (!topicId) return;
    setError("");
    setSubmitting(true);
    try {
      await setFeaturedTopic(token, position, Number(topicId));
      setIssueId("");
      onChanged();
    } catch (err) {
      setError(translateAuthError(err.message));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleClear() {
    if (!window.confirm("این جایگاه به حالت «به‌زودی» بازگردد؟")) return;
    setError("");
    try {
      await clearFeaturedTopic(token, position);
      onChanged();
    } catch (err) {
      setError(translateAuthError(err.message));
    }
  }

  return (
    <div className="admin-topic-section">
      <h2>{POSITION_LABELS[position]}</h2>

      {error && <p className="error">{error}</p>}

      {current ? (
        <div className="admin-article-row">
          <span>
            «{current.title_fa || current.title_en}» — دستهٔ {current.category}، شمارهٔ {current.issue_title} (#{current.issue_number})
          </span>
          <div className="admin-actions">
            <button type="button" onClick={handleClear}>
              حذف و بازگشت به «به‌زودی»
            </button>
          </div>
        </div>
      ) : (
        <p className="admin-empty">در حال حاضر «به‌زودی» نمایش داده می‌شود.</p>
      )}

      <form onSubmit={handleSet}>
        <div className="auth-field">
          <label htmlFor={`issue-${position}`}>شماره</label>
          <select id={`issue-${position}`} value={issueId} onChange={(e) => setIssueId(e.target.value)}>
            <option value="">انتخاب کنید...</option>
            {issues.map((iss) => (
              <option value={iss.id} key={iss.id}>
                {iss.title} (#{iss.number})
              </option>
            ))}
          </select>
        </div>

        <div className="auth-field">
          <label htmlFor={`topic-${position}`}>کارت</label>
          <select
            id={`topic-${position}`}
            value={topicId}
            onChange={(e) => setTopicId(e.target.value)}
            disabled={!issueId || loadingTopics}
          >
            <option value="">{loadingTopics ? "در حال بارگذاری..." : "انتخاب کنید..."}</option>
            {topics.map((t) => (
              <option value={t.id} key={t.id}>
                {t.category} — {t.title_fa || t.title_en}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="auth-submit" disabled={!topicId || submitting}>
          {submitting ? "در حال ثبت..." : current ? "جایگزینی این کارت" : "نمایش در صفحهٔ اصلی"}
        </button>
      </form>
    </div>
  );
}

// Section C (admin-only): picks which three issue topic cards (from any
// شماره) fill the homepage's hero-poster slots — see HomePage.js and
// HeroPosterCard.js.
function AdminFeaturedPage() {
  const [issues, setIssues] = useState([]);
  const [featured, setFeatured] = useState({});
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");

  function load() {
    setLoading(true);
    Promise.all([getIssues(), getFeaturedTopics()])
      .then(([issuesList, featuredList]) => {
        setIssues(issuesList);
        const byPosition = {};
        for (const f of featuredList) {
          byPosition[f.position] = f;
        }
        setFeatured(byPosition);
      })
      .catch((err) => setError(translateAuthError(err.message)))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  return (
    <div className="admin-page">
      <Link to="/admin" className="back-link">
        ← بازگشت به پنل مدیریت
      </Link>

      <div className="admin-page-header">
        <h1>کارت‌های منتخب صفحهٔ اصلی</h1>
      </div>

      <p className="admin-empty">
        این سه جایگاه همان کارت‌های بالای صفحهٔ اصلی‌اند. هر کدام را می‌توانید به یکی از کارت‌های موجود در
        شماره‌های نشریه متصل کنید؛ جایگاهی که کارتی برایش انتخاب نشده «به‌زودی» نشان می‌دهد.
      </p>

      {error && <p className="error">{error}</p>}

      {loading
        ? <p>در حال بارگذاری...</p>
        : [1, 2, 3].map((position) => (
            <FeaturedSlot
              key={position}
              position={position}
              current={featured[position]}
              issues={issues}
              token={token}
              onChanged={load}
            />
          ))}
    </div>
  );
}

export default AdminFeaturedPage;
